import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import PointsBar from '../components/PointsBar';
import { useTripBuilder } from '../TripBuilderContext';

const statusSteps = [
  'Exploring', 'Destination Picked', 'Accounts Created', 'Availability Confirmed',
  'Points Transferred', 'Flights Booked', 'Hotels Booked', 'Seats Selected', 'Ready to Go'
];

export default function TripPlan({ trip }) {
  const { amexTotal: amex, chaseTotal: chase, userConfig } = useTripBuilder();
  const [dest, setDest] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const destId = trip?.selected_destination_id;
    Promise.all([
      destId ? api.getDestination(destId) : Promise.resolve(null),
      api.getChecklist(),
    ]).then(([d, c]) => { setDest(d); setChecklist(c); }).catch(() => {}).finally(() => setLoading(false));
  }, [trip]);

  const handleCheck = async (id, current) => {
    await api.updateChecklist(id, !current);
    setChecklist(prev => prev.map(c => c.id === id ? { ...c, is_completed: !current } : c));
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-brand-ink/65">Loading your plan…</div>;

  if (!dest) {
    return (
      <div className="text-center py-16 space-y-4">
        <div className="text-4xl">&#9992;</div>
        <h2 className="text-2xl font-bold">No destination selected yet</h2>
        <p className="text-brand-ink/70">Pick a destination from the Command Center to generate your trip plan.</p>
        <Link to="/" className="inline-block px-6 py-2 bg-teal text-navy font-bold rounded-lg hover:bg-teal-dark transition">
          Go to Command Center
        </Link>
      </div>
    );
  }

  const rec = dest.redemption_options?.[0];
  const hotel = dest.hotel_options?.find(h => h.is_recommended);
  const flightPoints = rec?.total_points_two_travelers || 0;
  const hotelPoints = hotel?.total_points || 0;
  const amexRemaining = amex - flightPoints;
  const chaseRemaining = chase - hotelPoints;

  const completedPct = checklist.length ? Math.round(checklist.filter(c => c.is_completed).length / checklist.length * 100) : 0;
  const currentStep = Math.min(Math.floor(completedPct / 12), statusSteps.length - 1);

  const categories = [...new Set(checklist.map(c => c.category))];

  return (
    <div className="space-y-8 pb-16">
      <Link to={`/destination/${dest.id}`} className="text-sm text-brand-ink/65 hover:text-brand-ink/75">&larr; Back to {dest.city}</Link>

      <div>
        <h1 className="text-2xl font-bold">Trip Plan: {dest.city}, {dest.country}</h1>
        <p className="text-brand-ink/70 text-sm mt-1">{userConfig.cabinClass.charAt(0).toUpperCase() + userConfig.cabinClass.slice(1)} &middot; {userConfig.travelers} traveler{userConfig.travelers === 1 ? '' : 's'}</p>
      </div>

      {/* Status Tracker */}
      <section className="bg-navy-light rounded-xl border border-brand-ink/10 p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-brand-ink/75">TRIP PROGRESS</span>
          <span className="text-sm text-teal font-bold">{completedPct}%</span>
        </div>
        <div className="h-2 bg-brand-ink/10 rounded-full overflow-hidden mb-3">
          <div className="h-full bg-teal rounded-full transition-all duration-500" style={{ width: `${completedPct}%` }} />
        </div>
        <div className="flex overflow-x-auto gap-1">
          {statusSteps.map((step, i) => (
            <div key={step} className={`flex-shrink-0 text-[10px] px-2 py-1 rounded ${i <= currentStep ? 'bg-teal/20 text-teal' : 'bg-brand-ink/5 text-brand-ink/55'}`}>
              {step}
            </div>
          ))}
        </div>
      </section>

      {/* Transfer Plan */}
      <section>
        <h2 className="text-xl font-bold mb-4">The Transfer Plan</h2>
        <div className="bg-navy-light rounded-xl border border-brand-ink/10 p-5 space-y-4">
          {/* Flight transfer */}
          {rec && (
            <div className="flex flex-col sm:flex-row items-stretch gap-3">
              <TransferBox label="Amex MR" value={amex.toLocaleString()} color="blue" />
              <Arrow label={`Transfer ${flightPoints.toLocaleString()}`} />
              <TransferBox label={rec.airline_program} value={`${flightPoints.toLocaleString()} pts`} color="teal" />
              <Arrow label="Book" />
              <TransferBox label="Flights" value={rec.routing_description} color="teal" small />
            </div>
          )}

          <div className="flex items-center gap-3 text-sm">
            <div className="flex-1"><PointsBar used={flightPoints} total={amex} label="Amex MR after flights" color="bg-blue-500" /></div>
            <div className="text-right text-xs text-brand-ink/65">{amexRemaining.toLocaleString()} remaining</div>
          </div>

          {/* Hotel transfer */}
          {hotel && (
            <>
              <div className="border-t border-brand-ink/10 pt-4"></div>
              <div className="flex flex-col sm:flex-row items-stretch gap-3">
                <TransferBox label="Chase UR" value={chase.toLocaleString()} color="indigo" />
                <Arrow label={`Transfer ${hotelPoints.toLocaleString()}`} />
                <TransferBox label={hotel.hotel_program} value={`${hotelPoints.toLocaleString()} pts`} color="indigo" />
                <Arrow label="Book" />
                <TransferBox label="Hotel" value={`${hotel.hotel_name} (${hotel.nights} nights)`} color="indigo" small />
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="flex-1"><PointsBar used={hotelPoints} total={chase} label="Chase UR after hotel" color="bg-indigo-500" /></div>
                <div className="text-right text-xs text-brand-ink/65">{chaseRemaining.toLocaleString()} remaining</div>
              </div>
            </>
          )}

          {/* Warning */}
          <div className="bg-danger/10 border border-danger/30 rounded-lg p-3 text-sm text-danger">
            &#9888; DO NOT TRANSFER until you complete the availability check steps below. Transfers are irreversible.
          </div>
        </div>
      </section>

      {/* Budget Summary */}
      <section className="bg-navy-light rounded-xl border border-brand-ink/10 p-5">
        <h2 className="text-lg font-bold mb-3">Budget Summary</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <BudgetItem label="Amex Spent" value={`${flightPoints.toLocaleString()} pts`} sub="on flights" />
          <BudgetItem label="Chase Spent" value={`${hotelPoints.toLocaleString()} pts`} sub="on hotel" />
          <BudgetItem label="Cash (taxes)" value={`$${rec?.total_estimated_taxes || 0}`} sub="flight taxes" />
          <BudgetItem label="Daily Spend" value={`$${dest.avg_daily_budget_min}-${dest.avg_daily_budget_max}`} sub="per day, 2 ppl" />
        </div>
        <div className="mt-4 pt-4 border-t border-brand-ink/10 grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-brand-ink/65">Points Remaining</div>
            <div className="text-lg font-bold text-teal">{amexRemaining.toLocaleString()} Amex + {chaseRemaining.toLocaleString()} Chase</div>
          </div>
          <div>
            <div className="text-xs text-brand-ink/65">Estimated Cash Value Saved</div>
            <div className="text-lg font-bold text-teal">~${((flightPoints * (parseFloat(rec?.cents_per_point) || 1.5) / 100) + (hotelPoints * 2 / 100)).toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
          </div>
        </div>
      </section>

      {/* Checklist */}
      <section>
        <h2 className="text-xl font-bold mb-4">Booking Checklist</h2>
        <div className="space-y-4">
          {categories.map(cat => (
            <div key={cat}>
              <h3 className="text-sm font-semibold text-brand-ink/70 uppercase tracking-wider mb-2">
                {cat === 'preparation' ? 'Preparation' : cat === 'accounts' ? 'Create Accounts' : cat === 'booking' ? 'Book Everything' : 'Final Steps'}
              </h3>
              <div className="space-y-1">
                {checklist.filter(c => c.category === cat).map(item => (
                  <label
                    key={item.id}
                    className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition ${
                      item.is_completed ? 'bg-teal/5' : 'bg-navy-light hover:bg-navy-lighter'
                    } border border-brand-ink/5`}
                  >
                    <input
                      type="checkbox"
                      checked={item.is_completed}
                      onChange={() => handleCheck(item.id, item.is_completed)}
                      className="mt-0.5 w-4 h-4 rounded border-brand-ink/20 text-teal focus:ring-teal bg-navy-lighter"
                    />
                    <div>
                      <div className={`text-sm font-medium ${item.is_completed ? 'line-through text-brand-ink/65' : ''}`}>
                        {item.label}
                      </div>
                      {item.description && (
                        <div className="text-xs text-brand-ink/65 mt-0.5">{item.description}</div>
                      )}
                      {item.label.includes('Transfer') && (
                        <div className="text-[10px] text-gold mt-1">&#9888; Irreversible — confirm availability first!</div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function TransferBox({ label, value, color, small }) {
  const bg = color === 'blue' ? 'bg-blue-900/40 border-blue-500/30' : color === 'indigo' ? 'bg-indigo-900/40 border-indigo-500/30' : 'bg-teal/10 border-teal/30';
  return (
    <div className={`${bg} border rounded-lg p-3 flex-1 min-w-0`}>
      <div className="text-xs text-brand-ink/65 font-medium">{label}</div>
      <div className={`${small ? 'text-xs' : 'text-sm'} font-semibold mt-0.5 truncate`}>{value}</div>
    </div>
  );
}

function Arrow({ label }) {
  return (
    <div className="flex items-center justify-center px-1 sm:px-2">
      <div className="text-center">
        <div className="text-brand-ink/55 text-lg hidden sm:block">&rarr;</div>
        <div className="text-brand-ink/55 text-lg sm:hidden">&darr;</div>
        <div className="text-[10px] text-brand-ink/65 whitespace-nowrap">{label}</div>
      </div>
    </div>
  );
}

function BudgetItem({ label, value, sub }) {
  return (
    <div>
      <div className="text-xs text-brand-ink/65">{label}</div>
      <div className="text-lg font-bold">{value}</div>
      <div className="text-xs text-brand-ink/55">{sub}</div>
    </div>
  );
}
